import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Key, RotateCcw, Sparkles } from 'lucide-react';
import { BrandMark, ThemeToggle } from '@rynora/theme';
import { getApiBaseUrl } from '../../../lib/config';
import './design-system.css';

interface Palette {
  id: 'violet' | 'blue' | 'emerald';
  label: string;
  vibe: string;
  light: {
    accent: string;
    accentStrong: string;
    accentLight: string;
    accentGlow: string;
    gradientStart: string;
    gradientEnd: string;
  };
  dark: {
    accent: string;
    accentStrong: string;
    accentLight: string;
    accentGlow: string;
    gradientStart: string;
    gradientEnd: string;
  };
}

const PALETTES: Palette[] = [
  {
    id: 'violet',
    label: 'Violet',
    vibe: 'AI-native. Distinct from competitor blues. Linear / Anthropic / Vercel neighbourhood.',
    light: {
      accent: '#7c3aed',
      accentStrong: '#6d28d9',
      accentLight: '#a78bfa',
      accentGlow: 'rgba(124, 58, 237, 0.18)',
      gradientStart: '#7c3aed',
      gradientEnd: '#d946ef',
    },
    dark: {
      accent: '#a78bfa',
      accentStrong: '#8b5cf6',
      accentLight: '#c4b5fd',
      accentGlow: 'rgba(167, 139, 250, 0.22)',
      gradientStart: '#a78bfa',
      gradientEnd: '#f0abfc',
    },
  },
  {
    id: 'blue',
    label: 'Classic blue',
    vibe: 'Familiar dev-tool aesthetic. Playwright / Cypress / Postman neighbourhood.',
    light: {
      accent: '#3b82f6',
      accentStrong: '#2563eb',
      accentLight: '#60a5fa',
      accentGlow: 'rgba(59, 130, 246, 0.18)',
      gradientStart: '#3b82f6',
      gradientEnd: '#06b6d4',
    },
    dark: {
      accent: '#60a5fa',
      accentStrong: '#3b82f6',
      accentLight: '#93c5fd',
      accentGlow: 'rgba(96, 165, 250, 0.22)',
      gradientStart: '#60a5fa',
      gradientEnd: '#22d3ee',
    },
  },
  {
    id: 'emerald',
    label: 'Emerald',
    vibe: '"Pass first" energy. Risk: clashes with status-green for test results.',
    light: {
      accent: '#059669',
      accentStrong: '#047857',
      accentLight: '#34d399',
      accentGlow: 'rgba(5, 150, 105, 0.18)',
      gradientStart: '#059669',
      gradientEnd: '#0ea5e9',
    },
    dark: {
      accent: '#34d399',
      accentStrong: '#10b981',
      accentLight: '#6ee7b7',
      accentGlow: 'rgba(52, 211, 153, 0.22)',
      gradientStart: '#34d399',
      gradientEnd: '#22d3ee',
    },
  },
];

function paletteCssVars(p: Palette, mode: 'light' | 'dark'): CSSProperties {
  const v = mode === 'light' ? p.light : p.dark;
  return {
    ['--accent' as string]: v.accent,
    ['--accent-strong' as string]: v.accentStrong,
    ['--accent-light' as string]: v.accentLight,
    ['--accent-glow' as string]: v.accentGlow,
    ['--gradient-start' as string]: v.gradientStart,
    ['--gradient-end' as string]: v.gradientEnd,
  };
}

function paletteJson(p: Palette): string {
  return JSON.stringify({ light: p.light, dark: p.dark }, null, 2);
}

// =============================================================================
// Live adoption helpers
// =============================================================================

const ADMIN_KEY_STORAGE_KEY = 'rynora-admin-key';

function readStoredAdminKey(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(ADMIN_KEY_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function writeStoredAdminKey(value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.sessionStorage.setItem(ADMIN_KEY_STORAGE_KEY, value);
    else window.sessionStorage.removeItem(ADMIN_KEY_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable (private mode); the user just re-pastes
    // next time. Non-fatal.
  }
}

function getApiBase(): string {
  return getApiBaseUrl();
}

interface CurrentTheme {
  light: Palette['light'] | null;
  dark: Palette['dark'] | null;
  pickedPalette: string | null;
  pickedAt: string | null;
}

interface StatusMessage {
  tone: 'success' | 'error' | 'info';
  text: string;
}

// =============================================================================
// Page
// =============================================================================

export function DesignSystemPage() {
  const [copied, setCopied] = useState<string | null>(null);
  const [adminKey, setAdminKey] = useState<string>(() => readStoredAdminKey());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [current, setCurrent] = useState<CurrentTheme | null>(null);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'Rynora — Design system preview';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  // Seed `current` from /api/theme so the user knows whether a palette is
  // already adopted (and which one) without having to inspect the DB.
  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBase()}/api/theme`, { cache: 'no-cache', credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: CurrentTheme | null) => {
        if (!cancelled && data) setCurrent(data);
      })
      .catch(() => {
        // Silent — adoption is optional, the editor still works without it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = async (id: string, json: string) => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // fall through; the user can still copy by selecting the <pre>.
    }
  };

  const handleAdopt = async (palette: Palette) => {
    if (!adminKey) {
      setStatus({ tone: 'error', text: 'Paste the admin API key above before adopting a palette.' });
      return;
    }
    setBusyId(palette.id);
    setStatus({ tone: 'info', text: `Adopting ${palette.label}…` });
    try {
      const res = await fetch(`${getApiBase()}/api/admin/config/theme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-api-key': adminKey,
        },
        body: JSON.stringify({
          light: palette.light,
          dark: palette.dark,
          pickedPalette: palette.id,
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as CurrentTheme;
        setCurrent(data);
        setStatus({
          tone: 'success',
          text: `${palette.label} is now live. Reload rynora.app on any tab to see it.`,
        });
        // Drop the locally-cached copy so the next page load picks up the
        // freshly-adopted palette without waiting the 60s cache window.
        try {
          window.localStorage.removeItem('rynora-content::api-theme');
        } catch {
          /* non-fatal */
        }
      } else if (res.status === 403) {
        setStatus({ tone: 'error', text: 'Admin key rejected. Double-check the value in Settings.' });
      } else if (res.status === 503) {
        setStatus({
          tone: 'error',
          text: 'Admin endpoints are not configured on this environment. Set ADMIN_API_KEY on the server.',
        });
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setStatus({ tone: 'error', text: body.error || `Adoption failed (HTTP ${res.status}).` });
      }
    } catch (err) {
      setStatus({ tone: 'error', text: `Network error: ${(err as Error).message}` });
    } finally {
      setBusyId(null);
    }
  };

  const handleRevert = async () => {
    if (!adminKey) {
      setStatus({ tone: 'error', text: 'Paste the admin API key above before reverting.' });
      return;
    }
    setBusyId('__revert');
    setStatus({ tone: 'info', text: 'Reverting to theme.json…' });
    try {
      const res = await fetch(`${getApiBase()}/api/admin/config/theme`, {
        method: 'DELETE',
        headers: { 'x-admin-api-key': adminKey },
      });
      if (res.ok) {
        const data = (await res.json()) as CurrentTheme;
        setCurrent(data);
        setStatus({
          tone: 'success',
          text: 'Reverted. The site now shows the palette committed to theme.json.',
        });
        try {
          window.localStorage.removeItem('rynora-content::api-theme');
        } catch {
          /* non-fatal */
        }
      } else if (res.status === 403) {
        setStatus({ tone: 'error', text: 'Admin key rejected.' });
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setStatus({ tone: 'error', text: body.error || `Revert failed (HTTP ${res.status}).` });
      }
    } catch (err) {
      setStatus({ tone: 'error', text: `Network error: ${(err as Error).message}` });
    } finally {
      setBusyId(null);
    }
  };

  const handleAdminKeyChange = (next: string) => {
    setAdminKey(next);
    writeStoredAdminKey(next);
  };

  const livePicked = current?.pickedPalette ?? null;

  return (
    <div className="ds-page">
      <header className="ds-page__header">
        <Link to="/" className="ds-page__home">
          <ArrowLeft size={16} />
          <BrandMark variant="compact" />
        </Link>
        <div className="ds-page__title">
          <span className="ds-page__eyebrow">Brand colour preview</span>
          <h1>Pick the palette that should ship</h1>
          <p>
            Each candidate renders the same component set so we can compare apples to apples. Try the theme toggle on
            the right to see light vs dark mode. When you've decided, either copy the JSON snippet into{' '}
            <code>packages/landing-page/public/content/theme.json</code> and commit, <em>or</em> hit{' '}
            <strong>Adopt for everyone</strong> below to flip the live site instantly via the admin endpoint.
          </p>
        </div>
        <ThemeToggle />
      </header>

      <section className="ds-admin-row">
        <label className="ds-admin-row__key">
          <Key size={14} />
          <span>Admin API key</span>
          <input
            type="password"
            placeholder="paste ADMIN_API_KEY (kept in sessionStorage, never logged)"
            value={adminKey}
            onChange={(e) => handleAdminKeyChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <div className="ds-admin-row__current">
          {livePicked ? (
            <>
              <span className="ds-admin-row__current-label">Live palette:</span> <strong>{livePicked}</strong>
              {current?.pickedAt ? (
                <span className="ds-admin-row__current-date">
                  {' · '}
                  {new Date(current.pickedAt).toLocaleString()}
                </span>
              ) : null}
              <button
                type="button"
                className="ds-btn ds-btn--ghost ds-btn--sm"
                onClick={handleRevert}
                disabled={busyId !== null}
              >
                <RotateCcw size={12} /> Revert to theme.json
              </button>
            </>
          ) : (
            <span className="ds-admin-row__current-label">
              Live palette: <strong>theme.json</strong> (no override active)
            </span>
          )}
        </div>
      </section>

      {status ? (
        <div className={`ds-status ds-status--${status.tone}`} role="status" aria-live="polite">
          {status.text}
        </div>
      ) : null}

      <main className="ds-page__grid">
        {PALETTES.map((p) => (
          <PaletteCard
            key={p.id}
            palette={p}
            isCopied={copied === p.id}
            isLive={livePicked === p.id}
            isBusy={busyId === p.id}
            onCopy={() => handleCopy(p.id, paletteJson(p))}
            onAdopt={() => handleAdopt(p)}
          />
        ))}
      </main>

      <footer className="ds-page__footer">
        <p>
          This page is <code>noindex</code> and not linked from anywhere public. Bookmark it; it's the source of truth
          for the chosen palette until the brand-colour pick is locked in.
        </p>
      </footer>
    </div>
  );
}

interface PaletteCardProps {
  palette: Palette;
  isCopied: boolean;
  isLive: boolean;
  isBusy: boolean;
  onCopy: () => void;
  onAdopt: () => void;
}

function PaletteCard({ palette, isCopied, isLive, isBusy, onCopy, onAdopt }: PaletteCardProps) {
  return (
    <article className={`ds-card${isLive ? ' ds-card--live' : ''}`} style={paletteCssVars(palette, 'light')}>
      <header className="ds-card__head">
        <div>
          <span className="ds-card__name">
            {palette.label}
            {isLive ? <span className="ds-card__live-tag">live</span> : null}
          </span>
          <p className="ds-card__vibe">{palette.vibe}</p>
        </div>
        <Swatches palette={palette} />
      </header>

      <PalettePreview mode="light" palette={palette} />
      <PalettePreview mode="dark" palette={palette} />

      <div className="ds-card__cta-row">
        <button
          type="button"
          className="ds-btn ds-btn--primary ds-btn--block"
          onClick={onAdopt}
          disabled={isBusy || isLive}
          title={isLive ? 'Already the live palette' : `Adopt ${palette.label} for everyone`}
        >
          <Sparkles size={14} />
          {isLive ? `${palette.label} is live` : isBusy ? 'Adopting…' : `Adopt ${palette.label} for everyone`}
        </button>
      </div>

      <details className="ds-card__json">
        <summary>
          <code>theme.json</code> snippet
          <button
            type="button"
            className="ds-card__copy"
            onClick={(e) => {
              e.preventDefault();
              onCopy();
            }}
          >
            {isCopied ? <Check size={14} /> : <Copy size={14} />}
            {isCopied ? 'Copied' : 'Copy'}
          </button>
        </summary>
        <pre>
          <code>{paletteJson(palette)}</code>
        </pre>
      </details>
    </article>
  );
}

function Swatches({ palette }: { palette: Palette }) {
  return (
    <div className="ds-swatches" aria-hidden="true">
      <span className="ds-swatch" style={{ background: palette.light.accent }} title={palette.light.accent} />
      <span
        className="ds-swatch"
        style={{ background: palette.light.accentStrong }}
        title={palette.light.accentStrong}
      />
      <span className="ds-swatch" style={{ background: palette.light.accentLight }} title={palette.light.accentLight} />
      <span
        className="ds-swatch ds-swatch--gradient"
        style={{ background: `linear-gradient(135deg, ${palette.light.gradientStart}, ${palette.light.gradientEnd})` }}
      />
    </div>
  );
}

function PalettePreview({ mode, palette }: { mode: 'light' | 'dark'; palette: Palette }) {
  return (
    <section className="ds-preview" data-theme={mode} style={paletteCssVars(palette, mode)}>
      <div className="ds-preview__label">{mode}</div>

      <div className="ds-mini-hero">
        <span className="ds-eyebrow">Hero · gradient</span>
        <h3>
          Test what matters, <span className="ds-mini-hero__hl">not how to script it.</span>
        </h3>
        <div className="ds-actions">
          <button type="button" className="ds-btn ds-btn--primary">
            Get started
          </button>
          <button type="button" className="ds-btn ds-btn--ghost">
            See demo
          </button>
        </div>
      </div>

      <div className="ds-row">
        <div className="ds-mini-card">
          <span className="ds-eyebrow">Card</span>
          <h4>Self-healing selectors</h4>
          <p>Visual + semantic + DOM resolution across 7 strategy layers.</p>
          <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm">
            Learn more
          </button>
        </div>

        <div className="ds-mini-pricing">
          <span className="ds-mini-pricing__badge">Most popular</span>
          <span className="ds-eyebrow">Pricing card</span>
          <h4>Team</h4>
          <div className="ds-mini-pricing__price">
            $79<span>/mo</span>
          </div>
          <button type="button" className="ds-btn ds-btn--primary ds-btn--block">
            Start trial
          </button>
        </div>
      </div>

      <div className="ds-pills">
        <span className="ds-pill ds-pill--ok">passed</span>
        <span className="ds-pill ds-pill--err">failed</span>
        <span className="ds-pill ds-pill--warn">self-healed</span>
        <span className="ds-pill ds-pill--info">skipped</span>
        <span className="ds-pill ds-pill--accent">queued</span>
      </div>

      <div className="ds-tabs" role="tablist">
        <button type="button" role="tab" aria-selected="true" className="ds-tab ds-tab--active">
          Run history
        </button>
        <button type="button" role="tab" className="ds-tab">
          Logs
        </button>
        <button type="button" role="tab" className="ds-tab">
          Variables
        </button>
      </div>

      <label className="ds-input-row">
        <span>Email</span>
        <input type="email" placeholder="you@example.com" defaultValue="qa@rynora.app" />
      </label>

      <pre className="ds-code">
        <code>{`$ rynora run "checkout-with-coupon"
✓ flow.steps[0]   navigate /products
✓ flow.steps[1]   add-to-cart
✓ flow.steps[2]   apply-coupon (self-healed)
✓ checkout completed in 4.2s`}</code>
      </pre>
    </section>
  );
}
